<template>
  <el-dialog 
    :title="title" 
    :visible.sync="dialogVisible" 
    width="75%"
    top="5vh"
    :before-close="handleClose">
    
    <!-- 搜索区域 -->
    <div class="search-section">
      <search-form 
        :search-data.sync="searchData"
        @search="handleSearch"
        @reset="handleReset">
      </search-form>
    </div>
    
    <!-- 表格区域 -->
    <div class="table-container">
      <el-table
        :data="tableData"
        v-loading="loading"
        @selection-change="handleSelectionChange"
        max-height="500"
        border
        style="width: 100%">
        <el-table-column type="selection" width="50" align="center"></el-table-column>
        <el-table-column prop="gatewayId" label="ID" min-width="60" align="center"></el-table-column>
        <el-table-column prop="name" label="网关名称" min-width="150" align="center"></el-table-column>
        <el-table-column prop="scenarioName" label="场景" min-width="140" align="center"></el-table-column>
        <el-table-column prop="modelName" label="模型" min-width="140" align="center"></el-table-column>
        <el-table-column prop="productType" label="网关类型" min-width="100" align="center"></el-table-column>
        <el-table-column prop="macAddr" label="网关地址" min-width="160" align="center"></el-table-column>
        <el-table-column prop="blackCount" label="黑名单屏蔽数" min-width="120" align="center"></el-table-column>
        <el-table-column prop="groupName" label="归属分组" min-width="120" align="center"></el-table-column>
        <el-table-column prop="nickname" label="创建人" min-width="80" align="center"></el-table-column>
        <el-table-column prop="phone" label="创建人手机" min-width="120" align="center"></el-table-column>
        <el-table-column prop="createTime" label="创建时间" min-width="150" align="center"></el-table-column>
      </el-table>
      
      <!-- 分页 -->
      <div class="pagination-container">
        <el-pagination
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
          :current-page="pagination.currentPage"
          :page-sizes="[10, 20, 30, 40, 50, 100]"
          :page-size="pagination.pageSize"
          layout="total, sizes, prev, pager, next, jumper"
          :total="pagination.total">
        </el-pagination>
      </div>
    </div>
    
    <div slot="footer">
      <el-button @click="handleClose">取消</el-button>
      <el-button type="primary" @click="handleConfirm">确定</el-button>
    </div>
  </el-dialog>
</template>

<script>
import dayjs from "dayjs";
import { gatewayListModelRelateGateway, gatewaySetRelateGateway } from "@/api/waveform/userScenario";
import SearchForm from "./SearchForm.vue";

export default {
  name: "GatewaySelector",
  components: {
    SearchForm
  },
  props: {
    visible: { type: Boolean, default: false },
    title: { type: String, default: '选择网关' },
    modelId: { type: [String, Number], default: null },
    modelName: { type: String, default: '' },
    scenarioId: { type: [String, Number], default: null }
  },
  data() {
    return {
      loading: false,
      tableData: [],
      selectedRows: [],
      pagination: { currentPage: 1, pageSize: 10, total: 0 },
      searchData: {
        gatewayId: '', name: '', productType: 'DFLRD', groupId: '', nickname: '',
        userId: '', phone: '', createTime: null, macAddr: '',
        scenarioName: '', modelName: ''
      }
    };
  },
  computed: {
    dialogVisible: {
      get() { return this.visible },
      set(val) { this.$emit('update:visible', val) }
    }
  },
  watch: {
    visible(newVal) {
      if (newVal && this.modelId) {
        this.loadGatewayList();
      }
    }
  },
  methods: {
    async loadGatewayList() {
      this.loading = true;
      
      try {
        const searchData = { ...this.searchData };
        
        if (this.searchData.createTime) {
          searchData.bindTimeStart = dayjs(this.searchData.createTime[0]).format("YYYY-MM-DD 00:00:00");
          searchData.bindTimeEnd = dayjs(this.searchData.createTime[1]).format("YYYY-MM-DD 23:59:59");
        }
        delete searchData.createTime;
        
        const params = {
          page: this.pagination.currentPage,
          size: this.pagination.pageSize,
          ...searchData
        };
        
        const response = await gatewayListModelRelateGateway(params);
        
        if (response.code === 200 && response.data) {
          this.tableData = response.data.records || [];
          this.pagination.total = response.data.total || 0;
        } else {
          this.$message.error(response.message || '加载网关列表失败');
          this.resetTableData();
        }
      } catch (error) {
        console.error('加载网关列表失败:', error);
        this.$message.error('加载网关列表失败，请稍后重试');
        this.resetTableData();
      } finally {
        this.loading = false;
      }
    },

    resetTableData() {
      this.tableData = [];
      this.pagination.total = 0;
    },

    handleSearch() {
      this.pagination.currentPage = 1;
      this.loadGatewayList();
    },

    handleReset() {
      this.searchData = {
        gatewayId: '', name: '', productType: 'DFLRD', groupId: '', nickname: '',
        userId: '', phone: '', createTime: null, macAddr: '',
        scenarioName: '', modelName: ''
      };
      this.pagination.currentPage = 1;
      this.loadGatewayList();
    },

    handlePageChange(page) {
      this.pagination.currentPage = page;
      this.loadGatewayList();
    },

    handleSizeChange(size) {
      this.pagination.pageSize = size;
      this.pagination.currentPage = 1;
      this.loadGatewayList();
    },

    handleSelectionChange(selection) {
      this.selectedRows = selection;
    },

    handleClose() {
      this.dialogVisible = false;
      this.resetData();
      this.$emit('close');
    },

    handleConfirm() {
      if (this.selectedRows.length === 0) {
        this.$message.warning('请选择至少一个网关');
        return;
      }
      
      this.callSetRelateGatewayApi();
    },

    async callSetRelateGatewayApi() {
      if (!this.modelId || !this.modelName) {
        this.$message.error('模型信息不完整，无法关联网关');
        return;
      }

      if (!this.scenarioId) {
        this.$message.error('场景信息不完整，无法关联网关');
        return;
      }

      const gatewayIdList = this.selectedRows.map(row => row.id);
      
      const params = {
        modelId: String(this.modelId),
        modelName: this.modelName,
        gatewayIdList: gatewayIdList,
        scenarioId: String(this.scenarioId)  //  子场景的id
      };

      try {
        this.loading = true;
        const response = await gatewaySetRelateGateway(params);
        
        if (response.code === 200) {
          this.$message.success('网关关联成功');
          this.$emit('confirm', this.selectedRows);
          this.handleClose();
        } else {
          this.$message.error(response.message || '网关关联失败');
        }
      } catch (error) {
        console.error('网关关联失败:', error);
        this.$message.error('网关关联失败，请稍后重试');
      } finally {
        this.loading = false;
      }
    },

    resetData() {
      this.selectedRows = [];
      this.tableData = [];
      this.searchData = {
        gatewayId: '', name: '', productType: 'DFLRD', groupId: '', nickname: '',
        userId: '', phone: '', createTime: null, macAddr: ''
      };
      this.pagination = { currentPage: 1, pageSize: 10, total: 0 };
    }
  }
};
</script>

<style scoped>
.search-section {
  margin-bottom: 20px;
}

.table-container {
  position: relative;
}

.pagination-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 16px;
}

.el-table >>> .el-table__header-wrapper .el-table__header th {
  background-color: #fafafa;
}
</style>